import { useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";

import NotificationItem from "../ui/NotificationItem";
import { groupNotificationsByDate } from "../lib/groupNotificationsByDate";
import {
  NOTIFICATION_FILTER_ALL,
  NOTIFICATION_FILTER_UNREAD,
} from "../model/notificationConstants";
import {
  listNotifications,
  loadMoreNotifications,
  markAllNotificationsReadAction,
  markNotificationRead,
  setNotificationFilter,
} from "../model/notificationActions";
import {
  selectIsMarkingRead,
  selectMarkAllError,
  selectMarkAllLoading,
  selectMarkReadError,
  selectNotificationFilter,
  selectNotificationHasNextPage,
  selectNotificationItems,
  selectNotificationListError,
  selectNotificationListLoaded,
  selectNotificationListLoading,
  selectNotificationListLoadingMore,
  selectNotificationLoadMoreError,
  selectNotificationUnreadCount,
} from "../model/notificationSelectors";

function NotificationRow({ notification, onMarkRead }) {
  const isMarking = useSelector(selectIsMarkingRead(notification.id));
  return (
    <NotificationItem
      notification={notification}
      onMarkRead={onMarkRead}
      isMarking={isMarking}
    />
  );
}

function NotificationList() {
  const dispatch = useDispatch();
  const items = useSelector(selectNotificationItems);
  const error = useSelector(selectNotificationListError);
  const loadMoreError = useSelector(selectNotificationLoadMoreError);
  const loaded = useSelector(selectNotificationListLoaded);
  const loading = useSelector(selectNotificationListLoading);
  const loadingMore = useSelector(selectNotificationListLoadingMore);
  const filter = useSelector(selectNotificationFilter);
  const hasNext = useSelector(selectNotificationHasNextPage);
  const unreadCount = useSelector(selectNotificationUnreadCount);
  const markAllLoading = useSelector(selectMarkAllLoading);
  const markAllError = useSelector(selectMarkAllError);
  const markReadError = useSelector(selectMarkReadError);

  const grouped = useMemo(() => groupNotificationsByDate(items), [items]);

  useEffect(() => {
    dispatch(listNotifications(filter));
  }, [dispatch, filter]);

  const handleFilterChange = useCallback(
    (nextFilter) => {
      if (nextFilter === filter) return;
      dispatch(setNotificationFilter(nextFilter));
    },
    [dispatch, filter],
  );

  const handleMarkRead = useCallback(
    (id) => {
      dispatch(markNotificationRead(id));
    },
    [dispatch],
  );

  const handleMarkAllRead = useCallback(() => {
    if (markAllLoading || unreadCount <= 0) return;
    dispatch(markAllNotificationsReadAction());
  }, [dispatch, markAllLoading, unreadCount]);

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreNotifications());
  }, [dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(listNotifications(filter));
  }, [dispatch, filter]);

  const toolbar = (
    <div className="feature-notifications__toolbar">
      <div
        className="feature-notifications__filters"
        role="tablist"
        aria-label="Фільтр сповіщень"
      >
        <button
          type="button"
          role="tab"
          aria-selected={filter === NOTIFICATION_FILTER_ALL}
          className={`feature-notifications__filter${
            filter === NOTIFICATION_FILTER_ALL
              ? " feature-notifications__filter--active"
              : ""
          }`}
          onClick={() => handleFilterChange(NOTIFICATION_FILTER_ALL)}
        >
          Усі
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === NOTIFICATION_FILTER_UNREAD}
          className={`feature-notifications__filter${
            filter === NOTIFICATION_FILTER_UNREAD
              ? " feature-notifications__filter--active"
              : ""
          }`}
          onClick={() => handleFilterChange(NOTIFICATION_FILTER_UNREAD)}
        >
          Непрочитані
        </button>
      </div>

      <button
        type="button"
        className="feature-notifications__mark-all"
        onClick={handleMarkAllRead}
        disabled={markAllLoading || unreadCount <= 0}
        data-loading={markAllLoading ? "true" : undefined}
        aria-label="Позначити всі прочитаними"
      >
        {markAllLoading ? (
          <Spinner size="sm" label="Позначаємо всі як прочитані" />
        ) : (
          "Позначити всі прочитаними"
        )}
      </button>
    </div>
  );

  if (error) {
    return (
      <div className="feature-notifications__panel">
        {toolbar}
        <div className="feature-notifications__state" role="alert">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити сповіщення"
            description={typeof error === "string" ? error : "Спробуйте ще раз."}
            size="md"
          />
          <button
            type="button"
            className="feature-notifications__retry"
            onClick={handleRetry}
          >
            Спробувати ще
          </button>
        </div>
      </div>
    );
  }

  if (!loaded && loading) {
    return (
      <div className="feature-notifications__panel">
        {toolbar}
        <div className="feature-notifications__state" aria-busy="true">
          <Spinner size="lg" label="Завантаження сповіщень" />
        </div>
      </div>
    );
  }

  if (loaded && items.length === 0) {
    return (
      <div className="feature-notifications__panel">
        {toolbar}
        <div className="feature-notifications__state">
          <EmptyState
            icon="fa-solid fa-bell"
            title={
              filter === NOTIFICATION_FILTER_UNREAD
                ? "Немає непрочитаних сповіщень"
                : "Поки немає сповіщень"
            }
            description={
              filter === NOTIFICATION_FILTER_UNREAD
                ? "Усі сповіщення вже прочитані."
                : "Тут зʼявлятимуться нові події щодо ваших обʼєктів та клієнтів."
            }
            size="md"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="feature-notifications__panel">
      {toolbar}

      {markAllError ? (
        <p className="feature-notifications__feedback feature-notifications__feedback--error" role="alert">
          Не вдалося позначити всі сповіщення прочитаними. Спробуйте ще раз.
        </p>
      ) : null}

      {markReadError ? (
        <p className="feature-notifications__feedback feature-notifications__feedback--error" role="alert">
          Не вдалося позначити сповіщення прочитаним. Спробуйте ще раз.
        </p>
      ) : null}

      <div
        className="feature-notifications__groups"
        data-feature="notification-list"
        aria-live="polite"
      >
        {grouped.map((group) => (
          <section
            key={group.key}
            className="feature-notifications__group"
            data-date-group={group.key}
            aria-labelledby={`notification-group-${group.key}`}
          >
            <h2
              id={`notification-group-${group.key}`}
              className="feature-notifications__group-title"
            >
              {group.label}
            </h2>
            <ul className="feature-notifications__list">
              {group.items.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onMarkRead={handleMarkRead}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>

      {hasNext ? (
        <div className="feature-notifications__load-more-wrap">
          <button
            type="button"
            className="feature-notifications__load-more"
            onClick={handleLoadMore}
            disabled={loadingMore}
            data-loading={loadingMore ? "true" : undefined}
            aria-label="Завантажити ще сповіщення"
          >
            {loadingMore ? (
              <Spinner size="sm" label="Завантаження додаткових сповіщень" />
            ) : (
              "Завантажити ще"
            )}
          </button>
          {loadMoreError ? (
            <p className="feature-notifications__feedback feature-notifications__feedback--error" role="alert">
              Не вдалося завантажити додаткові сповіщення. Спробуйте ще раз.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default NotificationList;
